import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '@cotera/client/v0/lib/utils';
import type { HijackerQueueItem, HijackerWizardContext } from './types';
import {
  HijackerStepIndicator,
  CompactStepIndicator,
} from './hijacker-step-indicator';

export type HijackerWizardProps = {
  /** The currently active queue item */
  activeItem: HijackerQueueItem | null;
  /** Index of the active item */
  activeIndex: number;
  /** Total number of items in the queue */
  totalItems: number;
  /**
   * Stable forward-counting progress for the step indicator. Decoupled from the
   * live queue (which shrinks as items resolve) so the indicator counts up
   * 1 → 2 → 3 instead of counting the remaining items down.
   */
  stepIndicator: {
    current: number;
    total: number;
    completed: number;
  };
  /** Whether the current step can be completed */
  canComplete: boolean;
  /** Set whether the current step can be completed (validation gate) */
  setCanComplete: (canComplete: boolean) => void;
  /** Navigation callbacks */
  onComplete: () => void;
  onSkip: () => void;
  onPrevious: () => void;
  onDismissAll: () => void;
  /** Register custom skip button from child hijacker */
  registerSkipButton: (button: React.ReactNode | null) => void;
  /** Register custom submit button from child hijacker */
  registerSubmitButton: (button: React.ReactNode | null) => void;
  /** Chat model to pass to hijacker */
  chatModel: {
    addToolResult: (toolCallId: string, result: unknown) => void;
    id: string;
    model: string;
  };
  /** Whether the chat is readonly */
  readonly?: boolean;
};

const wizardTransition = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.2, ease: 'easeOut' as const },
};

/**
 * Wizard wrapper for managing multiple input hijackers sequentially.
 * Shows the active hijacker with navigation controls and step indicator.
 */
export const HijackerWizard: React.FC<HijackerWizardProps> = ({
  activeItem,
  activeIndex,
  totalItems,
  stepIndicator,
  canComplete,
  setCanComplete,
  onComplete,
  onSkip,
  onPrevious,
  onDismissAll,
  registerSkipButton,
  registerSubmitButton,
  chatModel,
  readonly = false,
}) => {
  const wizardContext: HijackerWizardContext | undefined = useMemo(
    () => ({
      currentStep: activeIndex + 1,
      totalSteps: totalItems,
      isFirst: activeIndex === 0,
      isLast: activeIndex === totalItems - 1,
      canComplete,
      setCanComplete,
      onComplete,
      onSkip,
      onPrevious: activeIndex > 0 ? onPrevious : undefined,
      onDismissAll,
      registerSkipButton,
      registerSubmitButton,
    }),
    [
      activeIndex,
      totalItems,
      canComplete,
      setCanComplete,
      onComplete,
      onSkip,
      onPrevious,
      onDismissAll,
      registerSkipButton,
      registerSubmitButton,
    ]
  );

  if (activeItem === null) {
    return null;
  }

  const HijackerComponent = activeItem.component;
  const hasMultiple = stepIndicator.total > 1;

  return (
    <div className="flex min-h-0 w-full max-w-full flex-1 flex-col overflow-hidden rounded-lg border-b border-border/50">
      {/* Header with step indicator and dismiss */}
      {hasMultiple && (
        <div className="flex shrink-0 items-center justify-between border-b border-border/50 px-3 py-2">
          <div className="flex items-center gap-3">
            <HijackerStepIndicator
              currentStep={stepIndicator.current}
              totalSteps={stepIndicator.total}
              completedSteps={stepIndicator.completed}
            />
            <CompactStepIndicator
              currentStep={stepIndicator.current}
              totalSteps={stepIndicator.total}
            />
          </div>
        </div>
      )}

      {/* Hijacker content — min-h-0 so a single nested overflow-y (e.g. folder picker) owns scrolling */}
      <div
        className={cn(
          'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden',
          hasMultiple ? 'px-3 py-3' : ''
        )}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeItem.id}
            initial={wizardTransition.initial}
            animate={wizardTransition.animate}
            exit={wizardTransition.exit}
            transition={wizardTransition.transition}
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          >
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <HijackerComponent
                part={activeItem.part}
                chatModel={chatModel}
                readonly={readonly}
                embedded={false}
                wizard={wizardContext}
                conversationId={activeItem.conversationId}
              />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
